/**
 * La fermeture au clic **au dehors** — partagée par les deux panneaux.
 *
 * Un panneau ouvert qu'un clic ailleurs ne referme pas reste posé par-dessus
 * l'écran : l'opérateur croit avoir annulé, et le panneau attend toujours. La
 * règle est la même pour la liste et pour le calendrier, et elle est donc
 * écrite une fois — deux copies auraient été deux endroits où la corriger.
 *
 * **Le clic au dehors ne change rien.** Il ferme, il ne choisit pas : un
 * panneau qui retiendrait la dernière valeur survolée ferait choisir à la
 * place de l'opérateur, et sur un journal d'audit comme sur une journée
 * comptable, le choix est la seule chose qui compte.
 *
 * `pointerdown` et non `click` : le second n'arrive qu'après le relâchement, et
 * un panneau qui se referme au relâchement laisse voir un scintillement. La
 * capture est posée sur le document, retirée à la fermeture — un écouteur qui
 * survivrait au démontage garderait le composant en vie.
 */

import { useEffect } from 'react';
import type { RefObject } from 'react';

/** Ferme quand un pointeur se pose hors de [contenant], tant que [ouvert]. */
export function useCloseOnOutsidePointer(
  contenant: RefObject<HTMLElement | null>,
  ouvert: boolean,
  fermer: () => void,
): void {
  useEffect(() => {
    if (!ouvert) return undefined;
    const auDehors = (evenement: PointerEvent | MouseEvent): void => {
      const cible = evenement.target;
      if (cible instanceof Node && contenant.current?.contains(cible) === true) return;
      fermer();
    };
    document.addEventListener('pointerdown', auDehors, true);
    document.addEventListener('mousedown', auDehors, true);
    return () => {
      document.removeEventListener('pointerdown', auDehors, true);
      document.removeEventListener('mousedown', auDehors, true);
    };
  }, [contenant, ouvert, fermer]);
}
